import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WaitlistForm({ inputTestId, submitTestId, successTestId }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [position, setPosition] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setState("loading");
    try {
      await axios.post(`${API}/waitlist`, { email });
      const { data } = await axios.get(`${API}/waitlist/count`);
      setPosition(data.count);
      setState("done");
    } catch {
      setState("idle");
      setError("Something went wrong — please try again.");
    }
  };

  if (state === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        data-testid={successTestId}
        className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.08] px-5 py-4"
      >
        <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-400" />
        <p className="text-sm text-emerald-100">
          You're <span className="font-bold text-emerald-300">#{position}</span> on the waitlist.
          We'll email you the moment Fitnance lands on Google Play.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full">
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          data-testid={inputTestId}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-12 flex-1 rounded-full border border-white/10 bg-white/[0.04] px-5 text-sm text-slate-100 placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-emerald-500/50"
        />
        <button
          data-testid={submitTestId}
          type="submit"
          disabled={state === "loading"}
          className="group flex h-12 items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-950 transition-all duration-300 hover:scale-[1.03] hover:bg-emerald-400 hover:shadow-[0_0_24px_rgba(16,185,129,0.4)] disabled:opacity-60"
        >
          {state === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Notify me
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </>
          )}
        </button>
      </div>
      {error && (
        <p data-testid={`${inputTestId}-error`} className="mt-2 pl-2 text-xs text-red-400">
          {error}
        </p>
      )}
    </form>
  );
}

export default function Waitlist() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then(({ data }) => setCount(data.count))
      .catch(() => {});
  }, []);

  return (
    <section id="waitlist" className="relative py-24 sm:py-32 lg:py-40">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(16,185,129,0.08),transparent_60%)]" />
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
            Now on iOS · Android soon
          </p>
          <h2 className="text-2xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            Be first in line when the books open.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-neutral-400">
            Fitnance is live on the App Store — join the waitlist for
            founding-member pricing and a ping the moment Google Play opens.
          </p>
          <div className="mx-auto mt-10 max-w-lg">
            <WaitlistForm
              inputTestId="waitlist-section-email-input"
              submitTestId="waitlist-section-submit-button"
              successTestId="waitlist-success-badge"
            />
          </div>
          {count > 0 && (
            <p data-testid="waitlist-live-count" className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              {count} {count === 1 ? "person is" : "people are"} already in
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
